import { Modal, Stack, Button, Text, TextInput, PasswordInput, Group, Divider } from '@mantine/core';
import { IconBrandGoogleFilled } from '@tabler/icons-react';
import { useState } from 'react';
import { useAuthStore } from './store.ts';
import { env } from '../env.ts';
import { api } from '../api/client.ts';

/**
 * Mirrors the Angular legacy login flow:
 *   - Google sign-in is a hidden HTML form POST to /signin/google with
 *     _csrf and scope=openid profile email. Browser follows the 302 to
 *     Google natively.
 *   - Optional form login UI (behind VITE_DEV_LOGIN) so devs can still
 *     authenticate as the seeded admin/user accounts on local backends.
 */
const GOOGLE_SCOPE = 'openid profile email';

export function LoginModal() {
  const open = useAuthStore(s => s.loginModalOpen);
  const close = useAuthStore(s => s.closeLoginModal);

  return (
    <Modal opened={open} onClose={close} title="Sign in" centered size="sm">
      <Stack gap="md">
        <GoogleSignInForm />
        {env.devLogin && (
          <>
            <Divider label="or sign in with username and password" labelPosition="center" />
            <DevFormLogin />
          </>
        )}
      </Stack>
    </Modal>
  );
}

function GoogleSignInForm() {
  // Native HTML form submission — browser handles the 302 to Google.
  // Backend reads _csrf form param (CookieCsrfTokenRepository accepts either
  // X-XSRF-TOKEN header or _csrf form field).
  const csrf = readXsrfCookie() ?? '';
  return (
    <form action="/signin/google" method="POST" style={{ width: '100%' }}>
      <input type="hidden" name="_csrf" value={csrf} />
      <input type="hidden" name="scope" value={GOOGLE_SCOPE} />
      <Button
        type="submit"
        leftSection={<IconBrandGoogleFilled size={18} />}
        fullWidth
        variant="filled"
        color="blue"
      >
        Continue with Google
      </Button>
    </form>
  );
}

function DevFormLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const setUser = useAuthStore(s => s.setUser);
  const close = useAuthStore(s => s.closeLoginModal);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        j_username: username,
        j_password: password,
        'remember-me': 'true',
        submit: 'Login',
      });
      const res = await api.post('/api/authentication', body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (res.status !== 200) {
        setError('Login failed. Check the credentials.');
        return;
      }
      const me = await api.get('/api/account');
      if (me.status === 200) {
        setUser(me.data);
        close();
      } else {
        setError('Session not established.');
      }
    } catch {
      setError('Network error.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <Stack gap="sm">
        <TextInput label="Login" value={username} onChange={e => setUsername(e.currentTarget.value)} required />
        <PasswordInput label="Password" value={password} onChange={e => setPassword(e.currentTarget.value)} required />
        {error && <Text c="red" size="sm">{error}</Text>}
        <Group justify="flex-end">
          <Button type="submit" loading={submitting}>Sign in</Button>
        </Group>
      </Stack>
    </form>
  );
}

function readXsrfCookie(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
  return match ? decodeURIComponent(match[1]!) : null;
}
